// Patient detail on the Conversions page shell: MainLayout, a single header
// row carrying the patient name and horizontal tabs, then the content.
//
// The old numbered step column (TabsPanel in DetailsLayout's leftColumn) is
// gone — it consumed ~256px of width on tables that need it, and a patient
// record is not a wizard, so numbered steps implied an order that never
// existed.
import { useEffect, useState } from "react";
import MainLayout from "Layouts/MainLayout/MainLayout";
import { useHistory, useParams } from "react-router-dom";
import { Content } from "Components/Patients/EditViewPatient/Content/Content";
import { usePatientContext } from "Context/Patient";
import { useLocalStorage } from "CustomHooks/LocalStorageHook";
import { TABS } from "Constants/Patient";
import { PATIENT_DETAIL_ACTIONS_SLOT } from "Components/Patients/EditViewPatient/Forms/DetailSection/DetailSection";
import { BASE_PATIENT_PATH } from "Constants/Views";
import { GetPatientById } from "Services/API/Patients.service";
import { ReactComponent as BackIcon } from "Assets/BackArrow.svg";
import ClassNames from "Helpers/Common";
import "Colors.css";

export function TheEditPatientPage() {
  const [activeTabIndex, setActiveTabIndex] = useLocalStorage(
    "patientFormStep",
    0
  );
  const { getPatientData, setActivePatient, setGeneralInfo, setActiveView } =
    usePatientContext();
  const history = useHistory();
  const { id: routeId } = useParams();

  // Deep-link support: the tabs read the patient from context (seeded by the
  // list page). On a fresh URL load that context is empty or stale, so hydrate
  // it from the :id in the URL before rendering the content — otherwise child
  // tables crash reading getPatientData().id.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const current = getPatientData();
    const needsHydrate = !current || String(current.id) !== String(routeId);
    if (needsHydrate) {
      // Seed the id SYNCHRONOUSLY (setActivePatient writes localStorage
      // immediately) so the id-based tabs never read an empty context, then
      // backfill the name/profile once it loads.
      setActivePatient({ id: routeId, name: "" });
      GetPatientById(routeId)
        .then((p) => {
          if (cancelled || !p) return;
          setActivePatient({
            id: routeId,
            name: [p.firstName, p.lastName].filter(Boolean).join(" "),
          });
          setGeneralInfo(p);
        })
        .catch(() => {});
    }
    setReady(true);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const { name } = getPatientData() || {};

  const goBack = () => {
    // go BACK so the list page's URL (with its ?q=&page= search state) is
    // restored; push only when there is no in-app history (deep link)
    if (history.length > 1) {
      history.goBack();
    } else {
      setActiveView(BASE_PATIENT_PATH);
    }
  };

  return (
    <MainLayout>
      {/* .PatientDetail scopes the tighter Form/Table wrapper padding to this
          page only (see Forms/FormCommonCss/Form.css). */}
      <div className="PatientDetail p-8 flex flex-col gap-8">
        {/* Name and tabs share one row (Conversions pattern) so the content
            starts higher on screen. */}
        <div className="flex justify-between items-center gap-3">
          {/* Tab pills are styled exactly like the patient LIST page's
              (Patients / Password Reset / Verification Codes) so the two
              pages read as one section. flex-1/min-w-0 keeps the actions slot
              on this row and lets the pills wrap inside their own box
              instead of pushing it to a line of its own. */}
          <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-6 gap-y-2 mr-4">
            <button
              type="button"
              onClick={goBack}
              className="flex items-center mr-2 focus:outline-none"
              title="Back to patients"
            >
              <BackIcon className="h-4 w-4 sapphire cursor-pointer" />
              <h1 className="ml-2 font-semibold text-2xl sapphire cursor-pointer">
                {name}
              </h1>
            </button>
            {TABS.map((tab, index) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTabIndex(index)}
                className={ClassNames(
                  // select-none: dragging across the tabs otherwise put a text
                  // caret / selection in them, which reads as an editable field.
                  "text-sm font-medium cursor-pointer select-none text-center mt-1 py-2 px-2 rounded-md focus:outline-none whitespace-nowrap",
                  index === activeTabIndex
                    ? "bg-gray-100 font-semibold text-black"
                    : "light_grayish_navy hover:bg-gray-50"
                )}
              >
                {tab}
              </button>
            ))}
          </div>
          {/* Per-tab actions (e.g. General Info's Edit Profile) portal in here
              so they sit on the tab row instead of floating above the content.
              The tab components own their own edit state, so a slot keeps that
              logic in place rather than lifting it up through Content. */}
          <div
            id={PATIENT_DETAIL_ACTIONS_SLOT}
            className="flex items-center gap-2 flex-shrink-0"
          />
        </div>

        <div className="flex flex-col gap-4">
          {ready ? (
            <Content
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={setActiveTabIndex}
            />
          ) : (
            <div className="text-sm text-gray-400 py-8">Loading patient…</div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
